import React, { useState, useEffect, useCallback } from "react";
import {
  PageHeader, KPIGrid, DataTable, StandardModal,
  Alert, Btn, FormField,
  inputStyle, textareaStyle, selectStyle,
  colors, spacing, typography, radius, shadows,
} from "../design-system";

const BASE         = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const PERF_API     = `${BASE}/auditor-performance`;
const AUDITORS_API = `${BASE}/auditors`;
const THRESHOLD   = 6;

// ── ScoreBar ──────────────────────────────────────────────────────────────────

function ScoreBar({ score, max = 10 }) {
  const pct   = Math.round((score / max) * 100);
  const color = score >= 8 ? colors.success : score >= THRESHOLD ? colors.warning : colors.danger;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
      <div style={{ flex: 1, background: colors.border, borderRadius: radius.xs, height: 8 }}>
        <div style={{ width: `${pct}%`, background: color, height: 8, borderRadius: radius.xs }} />
      </div>
      <span style={{ fontSize: typography.fontSizes.sm, fontWeight: typography.fontWeights.bold, color, minWidth: 24 }}>{score}</span>
    </div>
  );
}

// ── SummaryPanel ──────────────────────────────────────────────────────────────

function SummaryPanel({ summary, onClose }) {
  if (!summary) return null;
  return (
    <StandardModal
      title={`Performance Summary — ${summary.auditor_name}`}
      onClose={onClose}
      width={600}
      footer={<Btn variant="secondary" onClick={onClose}>Close</Btn>}
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: spacing.md, marginBottom: spacing.lg }}>
        {[
          { label: "Total Records",  value: summary.total_records, color: colors.primary },
          { label: "Average Score",  value: summary.average_score?.toFixed(1) ?? "—",
            color: summary.average_score >= THRESHOLD ? colors.success : colors.danger },
          { label: "Below Threshold", value: summary.below_threshold_count,
            color: summary.below_threshold_count > 0 ? colors.danger : colors.success },
        ].map(k => (
          <div key={k.label} style={{ background: colors.background, borderRadius: radius.md, padding: spacing.md, textAlign: "center", border: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: typography.fontSizes.xxl, fontWeight: typography.fontWeights.bold, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: typography.fontSizes.sm, color: colors.textMuted }}>{k.label}</div>
          </div>
        ))}
      </div>

      {summary.flagged && (
        <div style={{ background: colors.dangerLight, border: `1px solid #fca5a5`, borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`, marginBottom: spacing.lg, fontSize: typography.fontSizes.md, color: '#991b1b', fontWeight: typography.fontWeights.semibold }}>
          ⚠ This auditor has been auto-flagged — {summary.below_threshold_count >= 3 ? "3 consecutive" : "repeated"} scores below threshold ({THRESHOLD}). Their status may be suspended.
        </div>
      )}

      <div style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.semibold, color: colors.textPrimary, marginBottom: spacing.sm }}>Recent Scores</div>
      {summary.recent_scores.length === 0 ? (
        <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.md }}>No records yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
          {summary.recent_scores.map(s => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", background: colors.background, borderRadius: radius.md, padding: `${spacing.sm} ${spacing.md}`, fontSize: typography.fontSizes.md }}>
              <span style={{ color: colors.textMuted }}>Record #{s.id}{s.audit_id ? ` (Audit #${s.audit_id})` : ""}</span>
              <span style={{ fontWeight: typography.fontWeights.bold, color: s.average_score >= THRESHOLD ? colors.success : colors.danger }}>{s.average_score.toFixed(1)}/10</span>
              <span style={{ color: colors.textMuted }}>{new Date(s.created_at).toLocaleDateString()}</span>
            </div>
          ))}
        </div>
      )}
    </StandardModal>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function AuditorPerformance() {
  const [records, setRecords]         = useState([]);
  const [auditors, setAuditors]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filterAuditor, setFilterAuditor] = useState("");
  const [showCreate, setShowCreate]   = useState(false);
  const [showSummary, setShowSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [createForm, setCreateForm]   = useState({
    auditor_id: "", audit_id: "",
    score_quality: 7, score_timeliness: 7, score_accuracy: 7, score_witness: "",
    notes: "",
  });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState("");
  const [msgType, setMsgType] = useState("success");

  const token   = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAuditor) params.set("auditor_id", filterAuditor);
      const res = await fetch(`${PERF_API}/?${params}`, { headers });
      if (!res.ok) throw new Error("Failed to load performance records");
      setRecords(await res.json());
    } finally {
      setLoading(false);
    }
  }, [filterAuditor]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => {
    fetch(`${AUDITORS_API}/`, { headers }).then(r => r.json()).then(setAuditors).catch(() => {});
  }, []);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${PERF_API}/`, {
        method: "POST", headers,
        body: JSON.stringify({
          auditor_id:       parseInt(createForm.auditor_id),
          audit_id:         createForm.audit_id ? parseInt(createForm.audit_id) : null,
          score_quality:    parseInt(createForm.score_quality),
          score_timeliness: parseInt(createForm.score_timeliness),
          score_accuracy:   parseInt(createForm.score_accuracy),
          score_witness:    createForm.score_witness ? parseInt(createForm.score_witness) : null,
          notes:            createForm.notes || null,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Performance record saved.");
      setShowCreate(false);
      setCreateForm({ auditor_id: "", audit_id: "", score_quality: 7, score_timeliness: 7, score_accuracy: 7, score_witness: "", notes: "" });
      fetchRecords();
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleViewSummary(auditorId) {
    setSummaryLoading(true);
    try {
      const res = await fetch(`${PERF_API}/summary/${auditorId}`, { headers });
      if (!res.ok) throw new Error("Failed to load summary");
      setShowSummary(await res.json());
    } catch (e) { flash("Error: " + e.message, "error"); }
    finally { setSummaryLoading(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this performance record?")) return;
    try {
      const res = await fetch(`${PERF_API}/${id}`, { method: "DELETE", headers });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash("Record deleted.");
      fetchRecords();
    } catch (e) { flash("Error: " + e.message, "error"); }
  }

  const avgAll = records.length > 0
    ? (records.reduce((s, r) => s + r.average_score, 0) / records.length).toFixed(1)
    : null;
  const belowThreshold = records.filter(r => r.average_score < THRESHOLD).length;

  // Preview average for the create form
  const previewAvg = (() => {
    const scores = [parseInt(createForm.score_quality)||0, parseInt(createForm.score_timeliness)||0, parseInt(createForm.score_accuracy)||0];
    if (createForm.score_witness) scores.push(parseInt(createForm.score_witness));
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  })();

  function ScoreInput({ label, field }) {
    return (
      <FormField label={`${label}: ${createForm[field] || "—"}/10`}>
        <input
          type="range" min="1" max="10" step="1"
          value={createForm[field] || ""}
          onChange={e => setCreateForm(f => ({ ...f, [field]: e.target.value }))}
          style={{ width: "100%", accentColor: colors.primary }}
        />
      </FormField>
    );
  }

  const kpis = [
    { label: "Total Records",    value: records.length,                   icon: "📊", color: colors.primary },
    { label: "Fleet Average",    value: avgAll ? `${avgAll}/10` : "—",    icon: "⭐", color: avgAll >= THRESHOLD ? colors.success : avgAll ? colors.danger : colors.textMuted },
    { label: "Below Threshold",  value: belowThreshold,                   icon: "⚠️", color: belowThreshold > 0 ? colors.danger  : colors.success },
    { label: "Auditors Tracked", value: [...new Set(records.map(r => r.auditor_id))].length, icon: "👤", color: colors.purple },
  ];

  const columns = [
    { key: "id",          label: "#",         render: v => <span style={{ color: colors.textMuted, fontFamily: "monospace" }}>#{v}</span> },
    { key: "auditor_name", label: "Auditor",  render: (v, row) => (
      <div>
        <div style={{ fontWeight: typography.fontWeights.semibold, fontSize: typography.fontSizes.md }}>{v || `#${row.auditor_id}`}</div>
        <button onClick={() => handleViewSummary(row.auditor_id)} style={{ background: "none", border: "none", color: colors.primary, fontSize: typography.fontSizes.xs, cursor: "pointer", padding: 0 }}>View summary →</button>
      </div>
    )},
    { key: "audit_id",      label: "Audit",       render: v => v ? `#${v}` : "—" },
    { key: "score_quality",    label: "Quality",    render: v => <ScoreBar score={v} /> },
    { key: "score_timeliness", label: "Timeliness", render: v => <ScoreBar score={v} /> },
    { key: "score_accuracy",   label: "Accuracy",   render: v => <ScoreBar score={v} /> },
    { key: "score_witness",    label: "Witness",    render: v => v != null ? <ScoreBar score={v} /> : <span style={{ color: colors.textMuted }}>—</span> },
    { key: "average_score", label: "Average", render: v => {
      const color = v >= 8 ? colors.success : v >= THRESHOLD ? colors.warning : colors.danger;
      return <span style={{ fontSize: typography.fontSizes.lg, fontWeight: typography.fontWeights.bold, color }}>{v?.toFixed(1)}</span>;
    }},
    { key: "created_at", label: "Date", render: v => v ? new Date(v).toLocaleDateString() : "—" },
    { key: "_actions", label: "", align: "right", render: (_, row) => (
      <Btn size="sm" variant="danger" onClick={() => handleDelete(row.id)}>Delete</Btn>
    )},
  ];

  return (
    <div>
      <PageHeader
        title="Auditor Performance"
        subtitle={`Score auditors across surveillance audits. 3 consecutive scores below ${THRESHOLD} trigger auto-flagging.`}
        actions={<Btn onClick={() => setShowCreate(true)}>+ Add Score</Btn>}
      />

      <KPIGrid kpis={kpis} columns={4} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Filter */}
      <div style={{ display: "flex", gap: spacing.md, marginBottom: spacing.lg, alignItems: "center" }}>
        <select value={filterAuditor} onChange={e => setFilterAuditor(e.target.value)} style={{ ...selectStyle, width: "auto" }}>
          <option value="">All Auditors</option>
          {auditors.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
        </select>
        {filterAuditor && (
          <Btn variant="outline" onClick={() => handleViewSummary(filterAuditor)} loading={summaryLoading}>
            View Summary
          </Btn>
        )}
      </div>

      <DataTable columns={columns} rows={records} loading={loading} emptyMessage="No performance records found." keyField="id" />

      {/* Create Modal */}
      {showCreate && (
        <StandardModal
          title="Add Performance Record"
          onClose={() => setShowCreate(false)}
          width={600}
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Btn>
              <Btn type="submit" form="create-perf-form" loading={saving}>Save Record</Btn>
            </>
          }
        >
          <form id="create-perf-form" onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md }}>
                <FormField label="Auditor" required>
                  <select required value={createForm.auditor_id} onChange={e => setCreateForm(f => ({ ...f, auditor_id: e.target.value }))} style={selectStyle}>
                    <option value="">Select auditor...</option>
                    {auditors.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                  </select>
                </FormField>
                <FormField label="Audit ID" helper="Surveillance audit reference">
                  <input type="number" min="1" value={createForm.audit_id} onChange={e => setCreateForm(f => ({ ...f, audit_id: e.target.value }))} placeholder="Optional" style={inputStyle} />
                </FormField>
              </div>

              <div style={{ background: colors.background, borderRadius: radius.md, padding: spacing.md, border: `1px solid ${colors.border}` }}>
                <div style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, marginBottom: spacing.md }}>Performance Scores (1–10)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
                  <ScoreInput label="Quality"    field="score_quality" />
                  <ScoreInput label="Timeliness" field="score_timeliness" />
                  <ScoreInput label="Accuracy"   field="score_accuracy" />
                  <div>
                    <FormField label={`Witness Score: ${createForm.score_witness ? `${createForm.score_witness}/10` : "—"} (optional)`}>
                      <input
                        type="range" min="1" max="10" step="1"
                        value={createForm.score_witness || 5}
                        onChange={e => setCreateForm(f => ({ ...f, score_witness: e.target.value }))}
                        style={{ width: "100%", accentColor: colors.primary }}
                      />
                    </FormField>
                    <button type="button" onClick={() => setCreateForm(f => ({ ...f, score_witness: "" }))} style={{ background: "none", border: "none", color: colors.textMuted, fontSize: typography.fontSizes.xs, cursor: "pointer", padding: 0 }}>
                      Clear (no witness)
                    </button>
                  </div>
                </div>
                <div style={{ marginTop: spacing.md, fontSize: typography.fontSizes.md, color: colors.textSecondary }}>
                  Estimated average:{" "}
                  <strong style={{ color: parseFloat(previewAvg) >= THRESHOLD ? colors.success : colors.danger }}>
                    {previewAvg}/10
                  </strong>
                </div>
              </div>

              <FormField label="Notes">
                <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} rows={3} style={textareaStyle} />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Summary panel */}
      {showSummary && <SummaryPanel summary={showSummary} onClose={() => setShowSummary(null)} />}
    </div>
  );
}
